// Hue in degrees, saturation and value as fractions. The picker works in HSV because that
// is the space its square and slider map onto; the form only ever holds the hex.
export type Hsv = { hue: number; saturation: number; value: number }

export const DEFAULT_HSV: Hsv = { hue: 0, saturation: 0, value: 1 }

export const clampRatio = (value: number): number => Math.min(1, Math.max(0, value))

const HEX_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

export const isHexColor = (value: string): boolean => HEX_PATTERN.test(value.trim())

// `#rgb` is the shorthand the API accepts but no colour input does, so it is expanded here.
export const toLongHex = (value: string): string | undefined => {
  const hex = value.trim().toLowerCase()
  if (!isHexColor(hex)) return undefined

  return hex.length === 4 ? `#${[...hex.slice(1)].map((character) => character + character).join('')}` : hex
}

export const hexToHsv = (value: string): Hsv | undefined => {
  const hex = toLongHex(value)
  if (!hex) return undefined

  const red = Number.parseInt(hex.slice(1, 3), 16) / 255
  const green = Number.parseInt(hex.slice(3, 5), 16) / 255
  const blue = Number.parseInt(hex.slice(5, 7), 16) / 255

  const max = Math.max(red, green, blue)
  const span = max - Math.min(red, green, blue)

  const hue =
    span === 0 ? 0
    : max === red ? 60 * (((green - blue) / span + 6) % 6)
    : max === green ? 60 * ((blue - red) / span + 2)
    : 60 * ((red - green) / span + 4)

  return { hue, saturation: max === 0 ? 0 : span / max, value: max }
}

// Black and grey resolve to red, because neither states a hue of its own. A picker on
// either has to keep showing the hue it was last on, so this reports when there is none to
// read and the caller should use the one it remembers.
export const ownHue = (value: string): number | undefined => {
  const hsv = hexToHsv(value)
  return hsv !== undefined && hsv.saturation > 0 && hsv.value > 0 ? hsv.hue : undefined
}

export const readHsv = (value: string, rememberedHue: number): Hsv => ({
  ...(hexToHsv(value) ?? DEFAULT_HSV),
  hue: ownHue(value) ?? rememberedHue,
})

export const hsvToHex = ({ hue, saturation, value }: Hsv): string => {
  const channel = (offset: number) => {
    const position = (offset + hue / 60) % 6
    const level = value * (1 - saturation * Math.max(0, Math.min(position, 4 - position, 1)))
    return Math.round(level * 255)
      .toString(16)
      .padStart(2, '0')
  }

  return `#${channel(5)}${channel(3)}${channel(1)}`
}
